import { useEffect, useState } from 'react';
import filter from 'lodash/filter';
import map from 'lodash/map';
import { createStore, createStateHook, createActionsHook } from 'react-sweet-state';
import { initialState, actions } from '@codexporer.io/expo-link-stores';
import { getCache } from './cache';
import { getOwner } from './user-utils';
import { graphql } from './graphql';

const localInitialState = {
    notifications: [],
    isLoading: null,
    error: null,
    didInitialLoad: false
};

const notificationCenterFieldsQueryFragment = `
    fragment NotificationCenterFields on NotificationCenter {
        id
        title
        message
        isSeen
        action
        displayFromAppVersion
        sentAt
        _deleted
    }
`;

const getNotificationCenterByOwnerQuery = `
    ${notificationCenterFieldsQueryFragment}
    query QueryNotificationCenterByOwner(
        $owner: String!
        $limit: Int
        $nextToken: String
    ) {
        result: notificationCenterByOwner(
            owner: $owner
            limit: $limit
            nextToken: $nextToken
            sortDirection: DESC
        ) {
            items {
                ...NotificationCenterFields
            }
            nextToken
        }
    }
`;

const updateNotificationCenterQuery = `
    mutation UpdateNotificationCenter
    (
        $input: MarkNotificationsAsSeenMutationInput!
    ) {
        result: markNotificationsAsSeen(
            input: $input
        ) {
            isSuccessful
        }
    }
`;

const setFromCache = () => async ({ setState }) => {
    if (getCache().isNotificationCenterRetrieved()) {
        return;
    }

    getCache().setNotificationCenterIsRetrieved();
    const notifications = await getCache().getNotificationCenter();
    setState({ notifications });
};

const setNotificationCenter = notifications => ({ setState }) => {
    setState({ notifications });
    getCache().setNotificationCenter(notifications);
};

const resetState = () => ({ setState }) => {
    setState(localInitialState);
};

const setIsLoading = isLoading => ({ setState }) => {
    setState({ isLoading });
};

const setError = error => ({ setState }) => {
    setState({ error });
};

const fetchNotificationCenter = () => async ({ getState, dispatch }) => {
    dispatch(setFromCache());

    const { isLoading } = getState();
    if (isLoading) {
        return;
    }

    const { owner } = await getOwner();
    const notifications = [];
    const fetchData = async () => {
        const result = await graphql({
            query: getNotificationCenterByOwnerQuery,
            variables: {
                owner,
                limit: 30
            }
        });

        notifications.push(
            ...filter(
                result.data.result?.items,
                ({ _deleted }) => _deleted !== true
            )
        );
    };

    try {
        dispatch(setError(null));
        dispatch(setIsLoading(true));

        await fetchData();

        dispatch(setIsLoading(false));
        dispatch(setNotificationCenter(notifications));
    } catch (error) {
        dispatch(setIsLoading(false));
        dispatch(setError(error));
    }
};

const updateNotificationCenter = () => async ({ dispatch, getState }) => {
    const { notifications } = getState();
    const notificationIds = map(notifications, 'id');

    try {
        const { isSuccessful } = (await graphql({
            query: updateNotificationCenterQuery,
            variables: {
                input: {
                    ids: notificationIds
                }
            }
        })).data.result;

        isSuccessful && dispatch(
            setNotificationCenter(
                map(notifications, notification => ({
                    ...notification,
                    isSeen: true
                }))
            )
        );
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
    }
};

const onInitialLoad = () => ({ setState, getState }) => {
    const { didInitialLoad } = getState();
    !didInitialLoad && setState({ didInitialLoad: true });
};

const Store = createStore({
    initialState: {
        ...initialState,
        ...localInitialState
    },
    actions: {
        ...actions,
        onInitialLoad,
        resetState,
        fetchNotificationCenter,
        updateNotificationCenter
    },
    name: 'NotificationCenter'
});

const useNotificationCenterState = createStateHook(
    Store,
    {
        selector: (
            {
                notifications,
                isLoading,
                error,
                didInitialLoad
            }
        ) => ({
            notifications,
            isLoading: isLoading || isLoading === null,
            error,
            didInitialLoad
        })
    }
);

export const useNotificationCenterActions = createActionsHook(Store);

export const useNotificationCenter = () => {
    const {
        fetchNotificationCenter,
        onInitialLoad
    } = useNotificationCenterActions();
    const {
        notifications,
        isLoading,
        error,
        didInitialLoad
    } = useNotificationCenterState();
    const [didFetch, setDidFetch] = useState(false);

    useEffect(() => {
        if (!didInitialLoad || error && !didFetch) {
            fetchNotificationCenter();
            onInitialLoad();
            setDidFetch(true);
        }
    }, [
        didFetch,
        didInitialLoad,
        error,
        fetchNotificationCenter,
        onInitialLoad
    ]);

    return {
        notifications,
        isLoading,
        error
    };
};

export const useFetchNotificationCenter = () => {
    const { fetchNotificationCenter } = useNotificationCenterActions();
    return fetchNotificationCenter;
};

export const useUpdateNotificationCenter = () => {
    const { updateNotificationCenter } = useNotificationCenterActions();
    return updateNotificationCenter;
};
